import {
    ArrowUturnLeftIcon,
    BoltIcon,
    ClockIcon,
    StopCircleIcon,
    XMarkIcon
} from '@heroicons/react/24/outline'
import { format, formatDistanceToNow, parseISO } from 'date-fns'
import { useEffect, useRef } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react'
import { useScheduledWorkHistoryQuery } from '../../api/client'
import CopyToClipboard from '../CopyToClipboard'
import { detailsFor } from './kind-details'
import { MIN_PANEL_HEIGHT, clampPanelHeight, maxPanelHeight } from './panel-height'
import { statusChipClasses } from './status-chip'
import { SCRAPE_COMMENT_FETCH_KIND, isRecurringScrapeCommentFetchTag } from './tags'
import type { WorkRow } from './work-row'

type Props = {
    /** The selected tag. Kept even when `row` is null so the panel can say so. */
    tag: string
    /** The row from the list, or null when the tag is not in the current list. */
    row: WorkRow | null
    actionInFlight: boolean
    /** False while a modal sits above the panel, so Escape closes that first. */
    escapeCloses: boolean
    /** The panel's height in pixels. Owned by the page, which also spaces the table for it. */
    height: number
    onHeightChange: (px: number) => void
    onClose: () => void
    onHistory: (tag: string) => void
    onHistoricFetch: (tag: string) => void
    onRunNow: (row: WorkRow) => void
    onStop: (row: WorkRow) => void
}

/** The newest runs shown in the panel; the modal has the rest. */
const RECENT_RUNS = 5

/** How far one arrow-key press on the drag handle moves the panel edge. */
const KEYBOARD_RESIZE_STEP = 24

const when = (value?: string | null): string => {
    if (!value) return '—'
    try {
        const d = parseISO(value)
        return `${formatDistanceToNow(d, { addSuffix: true })} · ${format(d, 'dd/MM/yyyy HH:mm')}`
    } catch {
        return value
    }
}

/**
 * Everything about the selected row, docked to the bottom of the page.
 *
 * Docked rather than a dialog so the list stays in view and usable:
 * clicking down the list changes the panel without a close-and-reopen.
 * Sticky to the viewport, because on a long list the panel would
 * otherwise sit far below the row that was clicked.
 *
 * The header and the right two columns are the same for every kind. The
 * settings column is the kind's own, from `kind-details`.
 */
function ScheduledWorkDetailsPanel({
    tag,
    row,
    actionInFlight,
    escapeCloses,
    height,
    onHeightChange,
    onClose,
    onHistory,
    onHistoricFetch,
    onRunNow,
    onStop
}: Props) {
    const asideRef = useRef<HTMLElement>(null)

    // The panel's bottom edge is wherever the scroll container puts it, so the
    // new height is measured from that edge rather than from the viewport.
    const startDrag = (event: ReactMouseEvent) => {
        event.preventDefault()
        const onMove = (move: MouseEvent) => {
            const bottom = asideRef.current?.getBoundingClientRect().bottom ?? window.innerHeight
            onHeightChange(clampPanelHeight(bottom - move.clientY, window.innerHeight))
        }
        const onUp = () => {
            window.removeEventListener('mousemove', onMove)
            window.removeEventListener('mouseup', onUp)
        }
        window.addEventListener('mousemove', onMove)
        window.addEventListener('mouseup', onUp)
    }

    const nudgeHeight = (event: ReactKeyboardEvent) => {
        const step =
            event.key === 'ArrowUp'
                ? KEYBOARD_RESIZE_STEP
                : event.key === 'ArrowDown'
                  ? -KEYBOARD_RESIZE_STEP
                  : 0
        if (step === 0) return
        event.preventDefault()
        onHeightChange(clampPanelHeight(height + step, window.innerHeight))
    }

    useEffect(() => {
        if (!escapeCloses) return
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [escapeCloses, onClose])

    const {
        data: historyData,
        loading: historyLoading,
        error: historyError
    } = useScheduledWorkHistoryQuery({
        variables: { tag },
        fetchPolicy: 'cache-and-network',
        // The Recent runs column is not rendered when the row is null, so
        // there is nothing for this query to feed.
        skip: !row
    })
    const runs = (historyData?.scheduledWorkHistory ?? []).slice(0, RECENT_RUNS)

    return (
        <aside
            ref={asideRef}
            className="sticky bottom-0 z-10 flex flex-col border-t border-ink-200 bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.06)]"
            style={{ height }}
            aria-label={`Details for ${tag}`}
        >
            {/* The grab bar. A separator with a value, so assistive tech reads it
                as the resizable edge it is; the arrow keys move it too. */}
            <div
                role="separator"
                aria-orientation="horizontal"
                aria-label="Resize details panel"
                aria-valuemin={MIN_PANEL_HEIGHT}
                aria-valuemax={maxPanelHeight(window.innerHeight)}
                aria-valuenow={Math.round(height)}
                tabIndex={0}
                onMouseDown={startDrag}
                onKeyDown={nudgeHeight}
                className="group flex h-3 shrink-0 cursor-row-resize items-center justify-center focus:outline-none focus-visible:bg-arwen-50"
                title="Drag to resize"
            >
                <span className="h-1 w-12 rounded-full bg-ink-300 group-hover:bg-ink-400" />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-3 pt-1">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-sm font-medium text-ink-900 break-all">{tag}</h2>
                            {row ? (
                                <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusChipClasses(
                                        row.status
                                    )}`}
                                >
                                    {row.status}
                                </span>
                            ) : null}
                        </div>
                        {row ? (
                            <p className="mt-0.5 flex flex-wrap items-center gap-x-1 text-xs text-ink-500">
                                <span className="text-ink-600">{row.summary}</span>
                                {row.nextRunAt ? (
                                    <span>· next run {when(row.nextRunAt)}</span>
                                ) : null}
                                <span title={`Row ID ${row.rowId} — the latest run of this work`}>
                                    · row <span className="font-mono">{row.rowId}</span>
                                </span>
                                <CopyToClipboard text={row.rowId} />
                            </p>
                        ) : (
                            <p className="mt-0.5 text-sm text-ink-500">
                                Not in this list. It may be hidden by the status filter, or its
                                rows may have been pruned.
                            </p>
                        )}
                    </div>
                    <div className="flex items-center space-x-3 shrink-0">
                        <button
                            onClick={() => onHistory(tag)}
                            className="inline-flex items-center"
                            title="History — every run, and why each one ended"
                            aria-label={`History for ${tag}`}
                        >
                            <ClockIcon className="h-5 w-5 text-ink-400 hover:text-ink-600" />
                        </button>
                        {row &&
                        row.kind === SCRAPE_COMMENT_FETCH_KIND &&
                        isRecurringScrapeCommentFetchTag(row.tag) ? (
                            <button
                                onClick={() => onHistoricFetch(row.tag)}
                                className="inline-flex items-center"
                                title="Historic fetch — one run, back to a date you choose"
                                aria-label={`Historic fetch for ${row.tag}`}
                                disabled={actionInFlight}
                            >
                                <ArrowUturnLeftIcon className="h-5 w-5 text-ink-400 hover:text-arwen-600" />
                            </button>
                        ) : null}
                        {row?.live ? (
                            <>
                                <button
                                    onClick={() => onRunNow(row)}
                                    className="inline-flex items-center"
                                    title="Run now"
                                    aria-label={`Run ${row.tag} now`}
                                    disabled={actionInFlight}
                                >
                                    <BoltIcon className="h-5 w-5 text-arwen-500 hover:text-arwen-700" />
                                </button>
                                <button
                                    onClick={() => onStop(row)}
                                    className="inline-flex items-center"
                                    title="Stop (no undo)"
                                    aria-label={`Stop ${row.tag}`}
                                    disabled={actionInFlight}
                                >
                                    <StopCircleIcon className="h-5 w-5 text-severe-500 hover:text-severe-700" />
                                </button>
                            </>
                        ) : null}
                        <button onClick={onClose} aria-label="Close details" title="Close (Esc)">
                            <XMarkIcon className="h-5 w-5 text-ink-400 hover:text-ink-600" />
                        </button>
                    </div>
                </div>

                {row ? (
                    <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-3">
                        <section>
                            <h3 className="text-xs font-medium uppercase tracking-wider text-ink-500 mb-2">
                                Settings
                            </h3>
                            <SettingsBody row={row} />
                        </section>
                        <section>
                            <h3 className="text-xs font-medium uppercase tracking-wider text-ink-500 mb-2">
                                Latest
                            </h3>
                            {row.lastRunStatus ? (
                                <p className="text-sm text-ink-900">
                                    <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mr-2 ${statusChipClasses(
                                            row.lastRunStatus
                                        )}`}
                                    >
                                        {row.lastRunStatus}
                                    </span>
                                    {when(row.lastRunAt)}
                                    {row.consecutiveFailures > 1
                                        ? ` · failed ×${row.consecutiveFailures}`
                                        : ''}
                                </p>
                            ) : (
                                <p className="text-sm text-ink-500">No run has finished yet.</p>
                            )}
                            {row.lastRunLog ? (
                                <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-ink-700 bg-ink-50 rounded p-2">
                                    {row.lastRunLog}
                                </pre>
                            ) : null}
                            {row.log && row.log !== row.lastRunLog ? (
                                <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-ink-500 bg-ink-50 rounded p-2">
                                    {row.log}
                                </pre>
                            ) : null}
                        </section>
                        <section>
                            <div className="flex items-baseline justify-between mb-2">
                                <h3 className="text-xs font-medium uppercase tracking-wider text-ink-500">
                                    Recent runs
                                </h3>
                                <button
                                    onClick={() => onHistory(tag)}
                                    className="text-xs text-arwen-600 hover:text-arwen-800"
                                >
                                    Full history
                                </button>
                            </div>
                            {historyLoading && runs.length === 0 ? (
                                <p className="text-sm text-ink-500">Loading…</p>
                            ) : historyError ? (
                                <p className="text-sm text-severe-700">
                                    Couldn't load recent runs.
                                </p>
                            ) : runs.length === 0 ? (
                                <p className="text-sm text-ink-500">No runs yet.</p>
                            ) : (
                                <ul className="divide-y divide-ink-100 text-sm">
                                    {runs.map((run) => (
                                        <li key={run.rowId} className="py-1.5">
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusChipClasses(
                                                        run.status
                                                    )}`}
                                                >
                                                    {run.status}
                                                </span>
                                                <span className="text-ink-700">
                                                    {when(
                                                        run.completedAt ??
                                                            run.startedAt ??
                                                            run.scheduledAt
                                                    )}
                                                </span>
                                            </div>
                                            {run.log ? (
                                                <div
                                                    className="mt-0.5 text-xs text-ink-500 truncate"
                                                    title={run.log}
                                                >
                                                    {run.log}
                                                </div>
                                            ) : null}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    </div>
                ) : null}
            </div>
        </aside>
    )
}

/** The kind's own settings body, looked up once per render. */
function SettingsBody({ row }: { row: WorkRow }) {
    const { Settings } = detailsFor(row.kind)
    return <Settings row={row} />
}

export default ScheduledWorkDetailsPanel
